import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from '@mui/lab';
import CheckIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import WarningIcon from '@mui/icons-material/Warning';
import { Button, Typography } from '@mui/material';
import dayjs from 'dayjs';

type PipelineStatus = 'success' | 'error' | 'in_progress' | 'pending' | 'partial_success';

interface PipelineStep {
  id: string;
  status: PipelineStatus;
  timestamp: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface DataPipelineProps {
  fileId: string;
  steps: PipelineStep[];
}

const getStatusIcon = (status: PipelineStatus) => {
  switch (status) {
    case 'success':
      return <CheckIcon sx={{ color: 'white' }} />;
    case 'error':
      return <ErrorIcon sx={{ color: 'white' }} />;
    case 'in_progress':
      return <PendingIcon sx={{ color: 'white' }} />;
    case 'pending':
      return <HourglassEmptyIcon sx={{ color: 'white' }} />;
    case 'partial_success':
      return <WarningIcon sx={{ color: 'white' }} />;
  }
};

const getStatusColor = (status: PipelineStatus) => {
  switch (status) {
    case 'success':
      return 'success.main';
    case 'error':
      return 'error.main';
    case 'in_progress':
      return 'primary.main';
    case 'pending':
      return 'grey.500';
    case 'partial_success':
      return 'warning.main';
  }
};

export const DataPipeline = ({ fileId, steps }: DataPipelineProps) => {
  console.log('[DataPipeline Render]', fileId)
  return (
    <Timeline>
      {steps.map((step, index) => (
        <TimelineItem key={step.id}>
          <TimelineSeparator>
            <TimelineDot sx={{ bgcolor: getStatusColor(step.status) }}>
              {getStatusIcon(step.status)}
            </TimelineDot>
            {index < steps.length - 1 && <TimelineConnector />}
          </TimelineSeparator>
          <TimelineContent>
            <Typography variant="subtitle1" component="div">
              {step.description}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dayjs(step.timestamp).format('MMM D, YYYY h:mm A')}
            </Typography>
            {step.action && (
              <Button
                variant="text"
                size="small"
                onClick={step.action.onClick}
                sx={{ mt: 1 }}
              >
                {step.action.label}
              </Button>
            )}
          </TimelineContent>
        </TimelineItem>
      ))}
    </Timeline>
  );
};

// Mock data for demonstration
export const mockPipelineData: DataPipelineProps = {
  fileId: 'FILE-123',
  steps: [
    {
      id: 'file-received',
      status: 'success',
      timestamp: '2025-02-26T14:30:00',
      description: 'File successfully received',
      action: {
        label: 'Download File',
        onClick: () => console.log('Downloading file FILE-123...')
      }
    },
    {
      id: 'file-processing',
      status: 'partial_success',
      timestamp: '2025-02-26T14:35:00',
      description: 'File processed with some warnings',
      action: {
        label: 'View Transactions',
        onClick: () => console.log('Navigating to transactions table for FILE-123...')
      }
    },
    {
      id: 'building-file',
      status: 'in_progress',
      timestamp: '2025-02-26T14:40:00',
      description: 'Building output file',
      action: {
        label: 'View Build Progress',
        onClick: () => console.log('Viewing build progress for FILE-123...')
      }
    },
    {
      id: 'file-delivery',
      status: 'pending',
      timestamp: '2025-02-26T15:00:00',
      description: 'Scheduled for delivery',
      action: {
        label: 'Run Manual Delivery',
        onClick: () => console.log('Triggering manual delivery for FILE-123...')
      }
    }
  ]
};

export default DataPipeline;
